import { Button, CheckIcon, CloseIcon, Input, style, TrashIcon, useOutsideClick } from "@r360/library";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import useAppSelector from "../../hooks/useAppSelector";
import useTranslate from "../../hooks/useTranslate";
import confirmationDialog from "../Utilities/ConfirmationDialog/ConfirmationDialog";
import "./PromotionCode.scss";

type TPromotionCodeInput = {
    promotionCode?: string;
    validating: boolean;
    promotionCodeError?: boolean;
    className?: string;
    onDarkBackground?: boolean;
    buttonClassName?: string;
    withoutMenuSearch?: boolean;
    onCodeSave: (code: string) => void;
    alwaysShowContent?: boolean;
    fullWidth?: boolean;
    autoFocus?: boolean;
};

export const PromotionCode: React.FC<TPromotionCodeInput> = ({
    promotionCode,
    validating = false,
    promotionCodeError = false,
    className,
    onDarkBackground = true,
    buttonClassName,
    withoutMenuSearch = false,
    onCodeSave,
    alwaysShowContent = false,
    fullWidth = false,
    autoFocus = true,
}) => {
    const t = useTranslate();
    const productsNotAddedPath = useAppSelector(state => state.checkoutCart.productsNotAddedPath);

    const [error, setError] = useState(promotionCodeError);
    const [input, setInput] = useState(promotionCode);
    const [isInteracting, setIsInteracting] = useState(false);
    const [showContent, setShowContent] = useState(alwaysShowContent || (!error && promotionCode && !isInteracting));

    const showValidatedCode = !error && promotionCode;

    useEffect(() => {
        if (!alwaysShowContent) {
            if (!promotionCode && !isInteracting) {
                setShowContent(false);
            } else if (promotionCode) {
                setShowContent(true);
            }
        }
    }, [promotionCode, isInteracting, alwaysShowContent]);

    useEffect(() => {
        if (promotionCodeError) {
            setError(true);
        }
    }, [promotionCodeError]);

    // Reset input after error message is shown.
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                resetInput();
            }, 5000);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [error]);

    // Reset input if no promotion code exists.
    useEffect(() => {
        if (!promotionCode) {
            resetInput();
        }
    }, [promotionCode]);

    const wrapperRef = useRef(null);

    useOutsideClick(true, wrapperRef, () => {
        setIsInteracting(false);
    });

    const handleInputShow = () => {
        !isInteracting && setIsInteracting(true);
        setShowContent(true);
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
    };

    const productsNotAddedWarningModal = () =>
        confirmationDialog({
            heading: t("book.leave_warning_header"),
            description: t("book.leave_warning_text"),
            confirmBtnText: t("book.leave_warning_confirm"),
            cancelBtnText: t("book.leave_warning_cancel"),
        });

    const saveCode = async () => {
        !isInteracting && setIsInteracting(true);

        if (!input) {
            return;
        }

        if (productsNotAddedPath) {
            const response = await productsNotAddedWarningModal();

            if (!response) {
                return;
            }
        }

        error && setError(false);
        onCodeSave(input);
    };

    const resetInput = () => {
        setInput("");
        setError(false);
    };

    const resetCode = async () => {
        !isInteracting && setIsInteracting(true);

        if (productsNotAddedPath) {
            const response = await productsNotAddedWarningModal();

            if (!response) {
                return;
            }
        }
        resetInput();
        onCodeSave("");
    };

    const promotionCodeClassName = classNames("promotion-code", className, {
        "promotion-code--full-width": fullWidth,
    });
    const promotionCodeValidatedClassName = classNames("promotion-code__validated-code", {
        "promotion-code__validated-code--on-dark-bg": onDarkBackground,
        "promotion-code__validated-code--without-menu-search": withoutMenuSearch,
    });
    const promotionCodeButtonClassName = classNames("promotion-code__button", buttonClassName, {
        "promotion-code__button--without-menu-search": withoutMenuSearch,
    });

    if (!showContent) {
        return (
            <Button
                customColorsReversed={onDarkBackground}
                type="tertiary"
                buttonClassName={promotionCodeButtonClassName}
                onClick={handleInputShow}
                disabled={validating}
                aria-expanded={!!showContent}
                aria-controls="promotion-code-input"
            >
                {t("book.search.campaign.do_you_have_code")}
            </Button>
        );
    }

    const renderValidatedCode = () => {
        return (
            <div className={promotionCodeValidatedClassName} role="status" aria-live="polite">
                <CheckIcon color={style.successColor} aria-hidden="true" />
                <p
                    className={classNames("u-mb-0", {
                        "u-color-black": !onDarkBackground,
                        "u-color-custom-primary-contrast": onDarkBackground,
                    })}
                >
                    {promotionCode}
                </p>
                <button
                    className="promotion-code__remove-btn"
                    onClick={resetCode}
                    aria-label={t("book.search.campaign.remove_code")}
                >
                    <TrashIcon
                        color={onDarkBackground ? style.customPrimaryContrastColor : style.darkBlueColor}
                        aria-hidden="true"
                    />
                </button>
            </div>
        );
    };

    const renderError = () => {
        return (
            <div className={promotionCodeValidatedClassName} role="alert" aria-live="assertive">
                <CloseIcon color={style.alertColor} size={30} aria-hidden="true" />
                <p
                    className={classNames("u-mb-0", {
                        "u-color-black": !onDarkBackground,
                        "u-color-custom-primary-contrast": onDarkBackground,
                    })}
                >
                    {t("book.search.campaign.not_found")}
                </p>
            </div>
        );
    };

    const renderInput = () => {
        return (
            <div id="promotion-code-input">
                <Input
                    label={t("book.search.campaign.title")}
                    value={input}
                    onChange={onInputChange}
                    inputSize="medium"
                    withSubmitButton={!!input}
                    submitButtonLabel={t("book.search.campaign.submit")}
                    onSubmitButtonPress={saveCode}
                    autoFocus={autoFocus}
                    allowCustomColors={onDarkBackground}
                    disabled={validating}
                    fullWidth={fullWidth}
                    aria-required="true"
                    aria-invalid={error}
                    aria-describedby={error ? "promotion-code-error" : undefined}
                />
            </div>
        );
    };

    const renderContent = () => {
        if (showValidatedCode) {
            return renderValidatedCode();
        }

        if (error) {
            return renderError();
        }

        return renderInput();
    };

    return (
        <div ref={wrapperRef} className={promotionCodeClassName}>
            {renderContent()}
        </div>
    );
};
